import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { RootDispatch } from "../store";
import { getDetailsOfRegisteredUserWithPaymentDetails } from "../store/slices/epcSlice";
import ResponseModel from "../models/response.model";

export const useInfiniteCustomers = (initialLimit = 20) => {
  const dispatch: RootDispatch = useDispatch();
  const [customers, setCustomers] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [selectedSalesRepId, setSelectedSalesRepId] = useState<string>("");

  const fetchCustomers = useCallback(
    async (
      pageNum: number,
      search: string,
      salesRepId: string,
      isLoadMore = false,
    ) => {
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const result: ResponseModel = await dispatch(
          getDetailsOfRegisteredUserWithPaymentDetails({
            page: pageNum,
            search: search || undefined,
            salesRepId: salesRepId || undefined,
          }),
        ).unwrap();

        const items = result?.data?.items || [];
        const totalPagesCount = result?.pagination?.totalPages || 1;
        setTotalPages(totalPagesCount);

        if (isLoadMore) {
          setCustomers((prev) => [...prev, ...items]);
        } else {
          setCustomers(items);
        }

        setHasMore(pageNum < totalPagesCount);
      } catch (error) {
        console.error("Error fetching customers:", error);
      } finally {
        if (isLoadMore) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [dispatch, initialLimit],
  );

  const loadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchCustomers(nextPage, searchTerm, selectedSalesRepId, true);
  }, [
    hasMore,
    isLoadingMore,
    page,
    searchTerm,
    selectedSalesRepId,
    fetchCustomers,
  ]);

  const handleSearch = useCallback(
    (search: string) => {
      setSearchTerm(search);
      setPage(1);
      setHasMore(true);
      fetchCustomers(1, search, selectedSalesRepId, false);
    },
    [fetchCustomers, selectedSalesRepId],
  );

  const handleSalesRepFilter = useCallback(
    (salesRepId: string) => {
      setSelectedSalesRepId(salesRepId);
      setPage(1);
      setHasMore(true);
      setSearchTerm(""); // Optional: clear search when changing sales rep
      fetchCustomers(1, "", salesRepId, false);
    },
    [fetchCustomers],
  );

  useEffect(() => {
    fetchCustomers(1, "", selectedSalesRepId, false);
  }, []);

  const reset = useCallback(() => {
    setCustomers([]);
    setPage(1);
    setHasMore(true);
    setSearchTerm("");
    fetchCustomers(1, "", "", false);
  }, [fetchCustomers]);

  return {
    customers,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    handleSearch,
    totalPages,
    reset,
    handleSalesRepFilter,
    selectedSalesRepId,
  };
};
